import { useEffect, useMemo, useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { PreviewTable } from '../../../../components/PreviewTable';
import {
  InlineAddTable,
  InlineRow,
} from '../../../../components/inline-add-table/InlineAddTable';
import {
  TdDate,
  TdInput,
  TdMoney,
  TdSelect,
} from '../../../../components/inline-add-table/cells';
import type { SelectOption } from '../../../../components/inputs/Select';
import { ApiError } from '../../../../lib/apiError';
import { formatMoney } from '../../../../lib/formatMoney';
import { uid } from '../../../../lib/uid';
import { focusValidationError } from '../../../../lib/validationFocus';
import { budgetAccountCodesApi } from '../budgetCatalogsApi';
import { ppasApi } from '../ppasApi';
import { PPA_EXPENSE_CLASS_LABEL, type PpaLineItemInput } from '../ppaTypes';
import { samplePpaLine } from '../utils/samplePpa';

/** `startDate` to `start-date`, exactly as `focusValidationError` does it. */
const kebab = (field: string) => field.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();

/**
 * The id of one inline cell — `ppa-lines-items.2.amount`.
 *
 * **This is a contract with the server.** `PpaLineItemsCreateRequestValidator` overrides
 * FluentValidation's indexer so a rejected line reads `items.2.amount` rather than
 * `Items[2].Amount`, and `focusValidationError` resolves that to `${idPrefix}-${kebab(field)}`.
 * Change either side and the reader is told which cell is wrong while nothing on screen moves
 * — which reads as the feature not being wired at all.
 */
const cellId = (index: number, column: string) => `ppa-lines-items.${index}.${kebab(column)}`;

interface DraftRow {
  key: string;
  item: string;
  budgetAccountCodeId: string | null;
  startDate: string;
  endDate: string;
  amount: number | null;
}

const EMPTY = (): DraftRow => ({
  key: uid('line'),
  item: '',
  budgetAccountCodeId: null,
  startDate: '',
  endDate: '',
  amount: null,
});

/**
 * Is this row filled in enough to count?
 *
 * The required column and nothing else, which is the definition reference §12 sets. A row
 * that fails it is either BLANK — pressed `+` and abandoned, which the server drops — or
 * half-filled, which the server refuses by naming the cell.
 */
const filled = (row: DraftRow) => row.item.trim().length > 0;

/**
 * Adds budgeted lines to a PPA, all under the PPA's own expense class.
 *
 * **The class is the PPA's FUND SOURCE (06 Oct 2026), stated and never picked.** The
 * reference's own modal says *"Filed under Personnel Services."* over a grid of `# · Account
 * code · Item · Start · End · Amount`; the request names no class at all, because the server
 * reads it off the PPA.
 *
 * **There is no Excluded column and no adjusted amount.** Both are BUDGET REVIEW's — a
 * department states what it is asking for and a reviewer states what it may have — so a
 * department's own form must not be able to write the verdict on its own ask.
 */
export function PpaLineItemsModal({
  open,
  ppaId,
  ppaNumber,
  ppaName,
  expenseClass,
  onClose,
  onSaved,
}: {
  open: boolean;
  ppaId: string;
  ppaNumber: string;
  ppaName: string;
  /** The PPA's fund source — the expense class every row is filed under. Stated, never sent. */
  expenseClass: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [rows, setRows] = useState<DraftRow[]>([EMPTY()]);
  const [accounts, setAccounts] = useState<SelectOption[]>([]);

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    /*
      Fails SOFT. The implied read on the chart of accounts is scoped to create and update, so
      a reader who may add a line holds it — but a failed request must not take the rest of
      the grid with it. The account code is optional, so an empty picker blocks nothing.
    */
    budgetAccountCodesApi
      .options(controller.signal)
      .then((options) => {
        if (controller.signal.aborted) return;

        setAccounts(
          options.map((a) => ({
            value: a.id,
            label: a.code,
            sublabel: a.description ?? undefined,
          })),
        );
      })
      .catch(() => undefined);

    return () => controller.abort();
  }, []);

  const validCount = rows.filter(filled).length;

  /*
    THE DRAFT TOTAL. An unsaved batch has no server figure to read, so the modal adds up what
    is on screen — once, here, so no two places on this form can disagree about it. Once the
    lines are saved the profile reads the SERVER's totals and this arithmetic is never
    consulted again.
  */
  const draftTotal = useMemo(
    () => rows.reduce((sum, row) => sum + (filled(row) ? row.amount ?? 0 : 0), 0),
    [rows],
  );

  const canSave = validCount > 0 && confirmed && !saving;

  function patch(key: string, change: Partial<DraftRow>) {
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...change } : row)));
  }

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    /*
      EVERY row is sent, blanks included, so the index a rejection names is the index of the
      row ON SCREEN. Dropping the blanks here would shift every later row's number and point
      the reader at the wrong cell; the server drops them after validating.
    */
    const items: PpaLineItemInput[] = rows.map((row) => ({
      item: row.item.trim(),
      budgetAccountCodeId: row.budgetAccountCodeId,
      startDate: row.startDate || null,
      endDate: row.endDate || null,
      amount: row.amount ?? 0,
    }));

    try {
      await ppasApi.addLineItems(ppaId, { items });
      onSaved();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not add these lines.', 0);

      setError(failure);

      focusValidationError(failure, {
        idPrefix: 'ppa-lines',
        /*
          The two refusals that name the BATCH rather than a cell — nothing to add, and the
          cap. Both are answered at the `+`, which is the control that fixes them,
          and mapping them is what stops `focusValidationError` logging a missing element for
          an id no cell was ever going to carry.
        */
        fieldIds: { items: 'ppa-lines-add' },
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add budgeted lines"
      size="3xl"
      id="ppa-lines-modal"
      autogenerate={{
        /*
          Fills the rows already on screen so the primary action enables on the first try, and
          resolves the account code against the LIVE catalog rather than inventing an id the
          server refuses.

          THE WINDOW IS LEFT EMPTY, deliberately. A budget is written for a fiscal year this
          form does not know — it has the PPA, not the plan — so any pair of dates it produced
          would be a guess presented as a fact. That is the "a field it cannot write honestly"
          case, and both dates are optional, so the absence blocks nothing.
        */
        onGenerate: () => {
          setRows((current) =>
            (current.length > 0 ? current : [EMPTY()]).map((row, index) => {
              const sample = samplePpaLine(index);

              return {
                ...row,
                item: filled(row) ? row.item : sample.item,
                amount: row.amount ?? sample.amount,
                budgetAccountCodeId:
                  row.budgetAccountCodeId ??
                  (accounts.length > 0
                    ? accounts[Math.floor(Math.random() * accounts.length)].value
                    : null),
              };
            }),
          );
        },
        disabled: saving,
      }}
      confirm={{
        id: 'ppa-lines-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="ppa-lines-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="ppa-lines-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {/*
          WHAT THE READER ALREADY DECIDED — the PPA, and its fund source, which is the class
          every line goes under. The Table Display View is for read-only facts on a form exactly as it is on a
          profile, and a class picker locked to one value would be a control collecting
          nothing.
        */}
        <PreviewTable
          variant="table-display"
          rows={[
            { label: 'PPA', value: ppaNumber, mono: true },
            { label: 'Activity', value: ppaName },
            { label: 'Filed under', value: PPA_EXPENSE_CLASS_LABEL[expenseClass] ?? null },
          ]}
        />

        <InlineAddTable
          headers={['Account code', 'Item *', 'Start', 'End', 'Amount']}
          colWidths={[180, 320, 150, 150, 160]}
          resizeKey="ppa-lines.colWidths.v1"
          flexColumn={1}
          addButtonId="ppa-lines-add"
          addDisabled={validCount < rows.length}
          rowCount={rows.length}
          onAddRow={() => setRows((current) => [...current, EMPTY()])}
        >
          {rows.map((row, index) => (
            <InlineRow
              key={row.key}
              idx={index + 1}
              onRemove={() => setRows((current) => current.filter((x) => x.key !== row.key))}
            >
              <TdSelect
                id={cellId(index, 'budgetAccountCodeId')}
                ariaLabel={`Account code, row ${index + 1}`}
                value={row.budgetAccountCodeId}
                onChange={(next) => patch(row.key, { budgetAccountCodeId: next })}
                options={accounts}
                disabled={saving}
              />

              <TdInput
                id={cellId(index, 'item')}
                ariaLabel={`Item, row ${index + 1}`}
                value={row.item}
                placeholder="What this line pays for"
                onChange={(next) => patch(row.key, { item: next })}
                disabled={saving}
              />

              <TdDate
                id={cellId(index, 'startDate')}
                ariaLabel={`Start date, row ${index + 1}`}
                value={row.startDate}
                onChange={(next) => patch(row.key, { startDate: next })}
                disabled={saving}
              />

              <TdDate
                id={cellId(index, 'endDate')}
                ariaLabel={`End date, row ${index + 1}`}
                value={row.endDate}
                onChange={(next) => patch(row.key, { endDate: next })}
                disabled={saving}
              />

              <TdMoney
                id={cellId(index, 'amount')}
                ariaLabel={`Amount, row ${index + 1}`}
                value={row.amount}
                onChange={(next) => patch(row.key, { amount: next })}
                disabled={saving}
              />
            </InlineRow>
          ))}
        </InlineAddTable>

        {/*
          THE TOTAL, BELOW THE TABLE, as a Table Display View — a read-only fact being SHOWN
          rather than a value being asked for, which is the test separating the two
          treatments. It is the DRAFT's own arithmetic and stops the moment the batch is
          saved: from then on the profile reads the server's figures.
        */}
        <PreviewTable
          variant="table-display"
          rows={[
            {
              label: `Total of ${validCount} ${validCount === 1 ? 'line' : 'lines'}`,
              value: formatMoney(draftTotal),
              mono: true,
            },
          ]}
        />

        <p className="text-xs text-slate-500">
          Every line here is filed under{' '}
          {PPA_EXPENSE_CLASS_LABEL[expenseClass] ?? 'this expense class'} &mdash; the PPA&rsquo;s
          fund source. A line under another class belongs on a PPA filed under that class.
        </p>
      </div>
    </Modal>
  );
}
